/**
 * CanonicalReviewPanel — Settings → Canonical mappings (brief 06).
 *
 * The human-review gate that replaces the cut Components page. Lists every
 * reviewable canonical mapping (proposed + confirmed) for the project's
 * primary binding and lets an admin promote proposals → confirmed, mark a
 * component as not-a-primitive, or override the canonical.
 *
 * Confirming a row is the trust boundary: only confirmed rows enter
 * `/api/catalog.canonicalMappings`, MCP conform, and the governance scan —
 * there is no separate publish step. Proposals are already exposed as
 * `proposedMappings`, so the action here reads as "promote proposed →
 * confirmed", not "publish".
 *
 * Read-only for demo orgs and non-admins. The buttons hide when the caller
 * can't mutate; the mutations are server-gated (`requireOrgRole admin` +
 * `guardReadOnly`) regardless, so this is UX, not the security boundary.
 */
import * as React from 'react';
import { Badge, Button, useToast } from '@fragments-sdk/ui';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { ContentState, PageHeader } from '@/components/layout';
import { OverridePicker } from '@/components/canonical';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  NOT_A_PRIMITIVE_CANONICAL,
  PRIMITIVE_BUCKET_LABEL,
} from '../../../../shared/canonicalPrimitiveStatus';
import settingsStyles from './settings.module.scss';
import s from './canonical-review.module.scss';

export interface CanonicalReviewPanelProps {
  orgId: Id<'orgs'> | undefined;
  isAdmin: boolean;
  isDemoOrg: boolean;
}

interface OverrideTarget {
  componentId: Id<'designSystemComponents'>;
  componentName: string;
  currentCanonical: string;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export function CanonicalReviewPanel({
  orgId,
  isAdmin,
  isDemoOrg,
}: CanonicalReviewPanelProps) {
  const { selectedProjectId } = useProjectContext();
  const { success, error } = useToast();

  const bindings = useQuery(
    api.projects.listBindingsByProject,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  // Multi-binding repos default to the primary binding for MVP (brief 06 §Q2).
  const bindingId =
    bindings?.find((binding) => binding.isPrimary)?._id ?? bindings?.[0]?._id;

  const rows = useQuery(
    api.canonicalPrimitiveMap.listForReview,
    selectedProjectId && bindingId
      ? { projectId: selectedProjectId, bindingId }
      : 'skip',
  );

  const confirmClassification = useMutation(
    api.canonicalPrimitiveMap.confirmClassification,
  );
  const markNotPrimitive = useMutation(
    api.canonicalPrimitiveMap.markNotPrimitive,
  );
  const confirmAllProposed = useMutation(
    api.canonicalPrimitiveMap.confirmAllProposedForBinding,
  );

  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [overrideTarget, setOverrideTarget] =
    React.useState<OverrideTarget | null>(null);

  const canMutate = isAdmin && !isDemoOrg;
  const proposedCount =
    rows?.filter((row) => row.status === 'proposed').length ?? 0;

  async function handleConfirm(
    componentId: Id<'designSystemComponents'>,
    name: string,
  ) {
    setBusyId(String(componentId));
    try {
      await confirmClassification({ componentId });
      success('Mapping confirmed', `${name} now ships to agents via the catalog.`);
    } catch (err) {
      error('Confirm failed', errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleMarkNotPrimitive(
    componentId: Id<'designSystemComponents'>,
    name: string,
  ) {
    setBusyId(String(componentId));
    try {
      await markNotPrimitive({ componentId });
      success('Marked not a primitive', `${name} removed from agent mappings.`);
    } catch (err) {
      error('Update failed', errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleConfirmAll() {
    if (!selectedProjectId || !bindingId) return;
    setBulkBusy(true);
    try {
      const result = await confirmAllProposed({
        projectId: selectedProjectId,
        bindingId,
      });
      success(
        `Confirmed ${result.confirmed} proposal${result.confirmed === 1 ? '' : 's'}`,
      );
    } catch (err) {
      error('Confirm all failed', errorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className={settingsStyles.detailContent}>
      <PageHeader
        title="Canonical mappings"
        titleAs="h2"
        description="Review the component → canonical-primitive map before it reaches agents. Confirming a proposal promotes it into the catalog, MCP conform, and the governance scan."
        size="md"
      />

      {!selectedProjectId ? (
        <ContentState
          className={settingsStyles.emptyState}
          title="Select a repository to review its canonical mappings."
          minHeight={160}
        />
      ) : rows === undefined ? (
        <ContentState
          className={settingsStyles.loadingState}
          title="Loading canonical mappings..."
          minHeight={160}
        />
      ) : rows.length === 0 ? (
        <ContentState
          className={settingsStyles.emptyState}
          title="No canonical mappings yet"
          description="Run a scan or classification on this repository and proposed primitives will appear here for review."
          minHeight={160}
        />
      ) : (
        <>
          <div className={s.toolbar}>
            <span className={s.toolbarMeta}>
              {rows.length} mapping{rows.length === 1 ? '' : 's'}
              {proposedCount > 0 ? ` · ${proposedCount} awaiting review` : ''}
            </span>
            {canMutate && proposedCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleConfirmAll}
                disabled={bulkBusy}
              >
                Confirm all proposed
              </Button>
            )}
          </div>

          {!canMutate && (
            <p className={s.readonlyNote} role="note">
              Read-only — confirming mappings requires an admin on a non-demo
              workspace.
            </p>
          )}

          <div className={settingsStyles.card}>
            {rows.map((row) => {
              const isNotPrimitive = row.canonical === NOT_A_PRIMITIVE_CANONICAL;
              const rowBusy =
                busyId === String(row.componentId) || bulkBusy;
              return (
                <div className={s.row} key={String(row.componentId)}>
                  <div className={s.identity}>
                    <span className={s.name}>{row.displayName}</span>
                    {row.filePath && (
                      <span className={s.file} title={row.filePath}>
                        {row.filePath}
                      </span>
                    )}
                  </div>
                  <span className={s.canonical}>
                    {isNotPrimitive ? '—' : row.canonical}
                  </span>
                  <Badge
                    variant={row.status === 'proposed' ? 'outline' : 'dim'}
                    size="sm"
                  >
                    {PRIMITIVE_BUCKET_LABEL[row.bucket]}
                  </Badge>
                  {canMutate && (
                    <div className={s.actions}>
                      {row.status === 'proposed' && !isNotPrimitive && (
                        <button
                          type="button"
                          className={s.actionBtn}
                          data-variant="confirm"
                          onClick={() =>
                            handleConfirm(row.componentId, row.displayName)
                          }
                          disabled={rowBusy}
                        >
                          Confirm
                        </button>
                      )}
                      <button
                        type="button"
                        className={s.actionBtn}
                        onClick={() =>
                          setOverrideTarget({
                            componentId: row.componentId,
                            componentName: row.displayName,
                            currentCanonical: row.canonical,
                          })
                        }
                        disabled={rowBusy}
                      >
                        Override
                      </button>
                      {!isNotPrimitive && (
                        <button
                          type="button"
                          className={s.actionBtn}
                          onClick={() =>
                            handleMarkNotPrimitive(
                              row.componentId,
                              row.displayName,
                            )
                          }
                          disabled={rowBusy}
                        >
                          Not a primitive
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {overrideTarget && orgId && (
        <OverridePicker
          componentId={overrideTarget.componentId}
          componentName={overrideTarget.componentName}
          orgId={orgId}
          currentCanonical={overrideTarget.currentCanonical}
          onClose={() => setOverrideTarget(null)}
          onComplete={() => {
            setOverrideTarget(null);
            success('Override saved');
          }}
        />
      )}
    </div>
  );
}
